import { useState, useEffect, useRef, useCallback } from 'react';
import { logEvent as diagLog } from '@/lib/diagnostics';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PreviewHeader, ZOOM_LEVELS } from '@/components/ui/PreviewHeader';
import { Download, FileText, AlertCircle, Sparkles, Loader2, Printer, Edit3, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { isMobilePrintUnsupported } from '@/lib/deviceUtils';
import { civilianizeCompetencies } from '@/lib/competencyMap';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';
import ResumeTranslationFeedbackPrompt, {
  hasSeenResumeTranslationFeedback,
  markResumeTranslationFeedbackSeen,
} from '@/components/ResumeTranslationFeedbackPrompt';
import { computeResumeInputSignature,
  getDismissedHash,
  setDismissedHash,
  clearDismissedHash,
} from '@/lib/resumeSignature';
import { resumeStyleVars, normalizeResumeStyle, RESUME_STYLES } from '@/lib/resumeStyles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Qualification categories render under "Military Qualifications"; everything else
// (decorations) renders under "Awards & Honors". Unknown/legacy -> decorations.
const QUAL_CATS = new Set(['badge', 'tab', 'qualification', 'warfare_qualification', 'military_school', 'specialty_qualification']);

/** Check if an array of objects has at least one entry with meaningful displayed content */
function hasMeaningfulEntries(arr, fields) {
  if (!arr || !Array.isArray(arr) || arr.length === 0) return false;
  return arr.some(item =>
    fields.some(f => {
      const v = item?.[f];
      return typeof v === 'string' ? v.trim().length > 0 : (v != null && v !== false);
    })
  );
}

function ReviewStep({ resume, onResumeUpdate, pendingResumeRegen, onRegenComplete, onDismissRegen, onMarkStale, user }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [awardCatalog, setAwardCatalog] = useState({});

  // Catalog for resolving qualification vs decoration category (legacy items lack stored category)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/resume/award-catalog`, {
          params: { q: '', limit: 500 }, withCredentials: true,
        });
        if (cancelled) return;
        const map = {};
        for (const a of resp.data.awards || []) map[a.award_id] = a;
        setAwardCatalog(map);
      } catch { /* graceful */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenError, setRegenError] = useState(null);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [displayResume, setDisplayResume] = useState(resume);
  const [zoomLevel, setZoomLevel] = useState('fit');
  // Presentation-only resume style (classic | modern | compact). Persisted via the
  // narrow PUT /api/resume/style endpoint — never touches content/scoring/completeness.
  const [resumeStyle, setResumeStyle] = useState(normalizeResumeStyle(resume?.resume_style));
  // Inline edit state — mirrors the Cover Letter pattern exactly.
  // A single "Edit" toggle puts the whole resume preview into edit mode
  // where every safely-persisted narrative block becomes editable:
  //   - professional_summary
  //   - military_roles[i].ai_summary_text   (rendered as bullet text in PDF/DOCX)
  // NOTE: manual text edits never touch the server's
  // `last_generated_input_hash` — freshness reflects builder-inputs ↔ AI
  // output agreement, not user prose edits. Read-only builder fields
  // (contact, dates, MOS title, education, awards, references, etc.)
  // remain non-editable here and are edited in their own builder steps.
  const [isEditing, setIsEditing] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  // Map<roleIndex, draftString>
  const roleDraftsRef = useRef({});
  const containerRef = useRef(null);

  // Guard against concurrent runRegeneration calls (first-time auto-gen + user click race)
  const inFlightRef = useRef(false);
  // One-shot guard for first-time auto-generation (declared early so retryGeneration can reset it)
  const firstGenTriggeredRef = useRef(false);
  // First-time resume-translation feedback prompt — shown ONCE per user
  // after a successful generation, gated by per-user localStorage flag.
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  // True ONLY for the very first auto-triggered AI run (no prior generated
  // content). Controls a richer reassurance wait state ("Building your
  // civilian resume…"). Regeneration on an existing resume keeps the
  // existing slim "Working…" banner to avoid alarming users.
  const [isFirstTimeGenerating, setIsFirstTimeGenerating] = useState(false);

  // Explicit user-triggered regeneration only
  const runRegeneration = useCallback(async (mode = 'manual') => {
    if (inFlightRef.current) {
      console.warn('[Review/Gen] Skipping duplicate runRegeneration call', { mode });
      return;
    }
    inFlightRef.current = true;
    const attemptId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID().slice(0, 8) : String(Date.now());
    const startedAt = Date.now();
    setIsRegenerating(true);
    setRegenError(null);
    console.info('[Review/Gen] start', { attemptId, mode, endpoint: '/api/resume/review-regen' });
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/resume/review-regen`,
        {},
        { withCredentials: true, timeout: 120000 }
      );
      const data = response.data;
      const elapsed = Date.now() - startedAt;
      console.info('[Review/Gen] success', {
        attemptId,
        mode,
        elapsedMs: elapsed,
        rolesRegenerated: data.roles_regenerated?.length || 0,
        summaryRegenerated: !!data.summary_regenerated,
        errors: data.errors?.length || 0,
      });
      if (data.resume) {
        setDisplayResume(data.resume);
        if (onResumeUpdate) onResumeUpdate(data.resume);
        // Server now holds the fresh `last_generated_input_hash` on the
        // returned resume document. Clear any per-browser dismissal so
        // future input changes will warn cleanly. We never write the
        // hash to localStorage anymore — server is the source of truth.
        try {
          if (data.resume.resume_id) clearDismissedHash(data.resume.resume_id);
        } catch (_) { /* fail-soft */ }
      }
      if (data.roles_regenerated?.length > 0) {
        toast.success(`${data.roles_regenerated.length} role summary(s) updated`);
      }
      if (data.summary_regenerated) {
        toast.success('Professional summary updated');
      }
      if (data.errors?.length > 0) {
        setRegenError('generation_unavailable');
      }
      // REQUIREMENT E — do NOT mislabel a successful no-op as an AI failure.
      // If regeneration was required (we only POST from the missing/stale path or the
      // explicit Continue-and-Regenerate modal) but the backend refreshed 0 roles / no
      // summary with 0 errors, that is an internal freshness/state inconsistency, not a
      // provider outage. Surface it as a diagnostic — never as "AI unavailable".
      const didRegenNothing = !(data.roles_regenerated?.length > 0) && !data.summary_regenerated;
      if (didRegenNothing && !(data.errors?.length > 0)) {
        try { diagLog('review_regen_noop_state_inconsistency', {
          mode,
          roles_regenerated: data.roles_regenerated?.length || 0,
          summary_regenerated: !!data.summary_regenerated,
          errors: data.errors?.length || 0,
          stale_discarded: !!data.stale_discarded,
          professional_summary_present: !!data.resume?.professional_summary,
          professional_summary_hash: data.resume?.professional_summary_hash || null,
        }); } catch (_) { /* fail-soft */ }
      }
      // First-time resume translation feedback prompt: only after a real
      // generation event (server confirms roles_regenerated or
      // summary_regenerated) and only if this user has never seen it.
      const didGenerate = (data.roles_regenerated?.length > 0) || !!data.summary_regenerated;
      if (didGenerate && !hasSeenResumeTranslationFeedback(user?.user_id)) {
        setShowFeedbackPrompt(true);
      }
      if (onRegenComplete) onRegenComplete();
    } catch (error) {
      const elapsed = Date.now() - startedAt;
      const code = error.code || error.response?.status || 'unknown';
      console.error('[Review/Gen] error', { attemptId, mode, elapsedMs: elapsed, code, message: error.message });
      setRegenError(code === 'ECONNABORTED' ? 'generation_timeout' : 'generation_unavailable');
    } finally {
      console.info('[Review/Gen] finally', { attemptId, mode, elapsedMs: Date.now() - startedAt });
      inFlightRef.current = false;
      setIsRegenerating(false);
      setIsFirstTimeGenerating(false);
      setShowRegenModal(false);
    }
  }, [onResumeUpdate, onRegenComplete, user]);

  // Retry handler for the error banner (secondary recovery after root-cause hardening)
  const retryGeneration = useCallback(() => {
    firstGenTriggeredRef.current = false;
    setRegenError(null);
    runRegeneration('retry');
  }, [runRegeneration]);

  useEffect(() => { if (resume) setDisplayResume(resume); }, [resume]);

  // Adopt a stored style when the resume (re)loads. Only when the user hasn't diverged
  // locally is irrelevant here — style is a single scalar; the server value is canonical
  // on load. Subsequent user changes go through handleStyleChange.
  useEffect(() => {
    if (resume?.resume_style) setResumeStyle(normalizeResumeStyle(resume.resume_style));
  }, [resume?.resume_style]);

  // Change + persist the presentation style. Immediate preview update; fire-and-forget
  // persistence. Never blocks navigation, never regenerates, never rescore-triggers.
  const handleStyleChange = useCallback(async (next) => {
    const style = normalizeResumeStyle(next);
    setResumeStyle(style);
    setDisplayResume((prev) => (prev ? { ...prev, resume_style: style } : prev));
    if (onResumeUpdate) onResumeUpdate((prev) => (prev ? { ...prev, resume_style: style } : prev));
    try {
      await axios.put(`${BACKEND_URL}/api/resume/style`, { resume_style: style }, { withCredentials: true });
    } catch (e) {
      toast.error('Could not save style preference');
    }
  }, [onResumeUpdate]);

  // ===== AUTOMATIC GENERATION (one-shot per mount) =====
  // Fires ONCE per ReviewStep mount whenever a GENERATABLE artifact is MISSING:
  //   - a military role has KSAs but no real ai_summary_text, OR
  //   - career data exists but there is no real professional_summary.
  // Stale artifacts already arrive here as MISSING: update_resume clears a role's
  // ai_summary_text when its canonical fingerprint changes, and get_resume gates a
  // stale professional_summary to null. So this single condition covers cold-start,
  // post-MOS-change, and stale-summary — with NO nonexistent-button dead-end.
  // It never fires for phone/style/visibility edits (those clear/gate no artifact, so
  // nothing is missing), and once everything is fresh a re-mount finds nothing missing
  // and makes zero AI calls. Coalesced via firstGenTriggeredRef (one-shot) + inFlightRef.
  // ===== CANONICAL ON-MOUNT SYNC (SPA-navigation parity with fresh mount/reload) =====
  // Normal in-app navigation into Review previously evaluated generation against the possibly-stale
  // in-memory `resume` prop. After a material career change the server gates stale narrative/summary
  // to null (get_resume + update_resume both apply gate_live_resume_narrative), but a stale parent
  // prop could still carry old content, so the "missing → auto-generate" check never fired — it only
  // worked once Diagnostics forced a full reload (fresh GET). This read-only GET fetches the SAME
  // server-gated canonical resume a reload would, so navigation behaves identically. It NEVER writes
  // or reverts canonical career data; generation stays gated until the fresh doc is in hand.
  const canonicalSyncedRef = useRef(false);
  const [canonicalReady, setCanonicalReady] = useState(false);

  useEffect(() => {
    if (canonicalSyncedRef.current) return;   // once only (survives StrictMode double-invoke)
    canonicalSyncedRef.current = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/resume`, { withCredentials: true });
        if (res?.data) {
          setDisplayResume(res.data);
          if (onResumeUpdate) onResumeUpdate(res.data);
        }
      } catch (e) {
        /* fall back to the in-memory prop on failure */
      } finally {
        setCanonicalReady(true);   // ALWAYS unblock generation, even on error
      }
    })();
  }, [onResumeUpdate]);

  useEffect(() => {
    if (firstGenTriggeredRef.current) return;
    if (!canonicalReady) return;
    if (!displayResume) return;
    if (isRegenerating) return;
    const PLACEHOLDER_SIGNALS = ['summary will be generated', 'summary content needs regeneration', 'click regenerate'];
    const isReal = (t) => {
      if (!t || typeof t !== 'string') return false;
      const trimmed = t.trim();
      if (trimmed.length < 20) return false;
      const lower = trimmed.toLowerCase();
      return !PLACEHOLDER_SIGNALS.some(s => lower.includes(s));
    };
    const roles = displayResume.military_roles || [];
    const hasPrior = !!(
      isReal(displayResume.professional_summary) ||
      roles.some(r => isReal(r?.ai_summary_text))
    );
    const roleNarrativeMissing = roles.some(r =>
      ((r?.selected_knowledge?.length > 0) || (r?.selected_skills?.length > 0) || (r?.selected_abilities?.length > 0))
      && !isReal(r?.ai_summary_text)
    );
    const hasCareerData = roles.length > 0 || (displayResume.civilian_experience || []).length > 0;
    const summaryMissing = hasCareerData && !isReal(displayResume.professional_summary);
    // "Was previously generated" survives the server's staleness gating: the gate nulls
    // ai_summary_text / professional_summary but LEAVES the freshness hashes intact. So a present
    // hash with now-missing text = a prior generated resume that a career change invalidated.
    const hadPriorGeneration = !!(
      displayResume.professional_summary_hash ||
      roles.some(r => !!r?.ai_summary_hash)
    );
    if (roleNarrativeMissing || summaryMissing) {
      firstGenTriggeredRef.current = true;          // claim the one-shot either way (no retrigger loop)
      setIsFirstTimeGenerating(!hadPriorGeneration);
      try { diagLog('review_generation_decision', {
        role_missing: roleNarrativeMissing, summary_missing: summaryMissing,
        has_prior_generated_resume: hadPriorGeneration,
        safeguard_preserve_dialog: hadPriorGeneration,
        auto_generation_started: !hadPriorGeneration,
        ui_copy_branch: !hadPriorGeneration ? 'first-time' : 'regen-preserve-dialog' }); } catch (e) {}
      if (hadPriorGeneration) {
        // SAFEGUARD: a prior generated resume exists — let the user preserve it BEFORE any
        // regeneration runs. No POST /api/resume/review-regen is issued until they choose
        // "Continue and Regenerate" in the ONE canonical preservation modal.
        setShowRegenModal(true);
      } else {
        runRegeneration('auto');                     // first-ever generation: nothing to preserve
      }
    } else {
      try { diagLog('review_generation_decision', {
        role_missing: roleNarrativeMissing, summary_missing: summaryMissing,
        has_prior_generated_resume: hasPrior, auto_generation_started: false,
        reason: 'all_fresh_nothing_missing' }); } catch (e) {}
    }
  }, [displayResume, isRegenerating, runRegeneration, canonicalReady]);

  // ===== STALE DETECTION (canonical artifact freshness) =====
  // Consistency fix: the "out of date" warning now uses the SAME freshness
  // dependency as generation, not the phone/email/street-inclusive input
  // signature. A generated artifact is out-of-date ONLY when it is actually
  // missing/stale — and stale artifacts already arrive here as missing because
  // the server clears a role's ai_summary_text when its canonical fingerprint
  // (branch/rank/mos_code/KSAs) changes and gates a stale professional_summary
  // (career fingerprint) to null. Presentation/contact-only edits (phone, email,
  // street, style, visibility) change NO fingerprint, so nothing is missing and
  // we never mark stale. When something IS missing the automatic-generation
  // effect above owns it (firstGenTriggeredRef), so we do not raise a manual
  // "click Regenerate" banner — no dead-end, no misleading instructions. The
  // separate regenError banner handles the rare generation-failure recovery.
  const staleCheckedRef = useRef(false);
  useEffect(() => {
    if (staleCheckedRef.current) return;
    if (!displayResume?.resume_id) return;
    if (isRegenerating) return;
    const roles = displayResume.military_roles || [];
    const roleMissing = roles.some(r =>
      ((r?.selected_knowledge?.length > 0) || (r?.selected_skills?.length > 0) || (r?.selected_abilities?.length > 0))
      && !isRealGeneratedText(r?.ai_summary_text)
    );
    const hasCareer = roles.length > 0 || (displayResume.civilian_experience || []).length > 0;
    const summaryMissing = hasCareer && !isRealGeneratedText(displayResume.professional_summary);
    staleCheckedRef.current = true;
    // Auto-generation (declared above) fires on the same missing condition and
    // flips firstGenTriggeredRef; only surface the manual banner if something is
    // genuinely missing AND auto-generation did not pick it up.
    if ((roleMissing || summaryMissing) && !firstGenTriggeredRef.current && onMarkStale) {
      onMarkStale();
    }
  }, [displayResume, isRegenerating, onMarkStale]);

  // Parse bullet points from AI summary text
  const parseSummaryBullets = (summaryText) => {
    if (!summaryText) return [];
    const lower = summaryText.toLowerCase();
    if (lower.includes('<!doctype') || lower.includes('<html') || lower.includes('ratelimiterror') || 
        lower.includes('rate_limit_exceeded') || (summaryText.match(/<[a-zA-Z/][^>]*>/g) || []).length > 3) {
      return [];
    }
    return summaryText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line && !line.startsWith('#'))
      .map(line => line.replace(/^[•\-\*▪◦●]\s*/, ''));
  };

  // Compute & civilianize core competencies
  const computeCoreCompetencies = (resumeData, maxItems = 12) => {
    if (!resumeData?.military_roles?.length) return [];
    const all = [];
    for (const role of resumeData.military_roles) {
      for (const item of (role.selected_abilities || [])) {
        const name = typeof item === 'object' ? item.element_name : item;
        if (name) all.push(name);
      }
      for (const item of (role.selected_skills || [])) {
        const name = typeof item === 'object' ? item.element_name : item;
        if (name) all.push(name);
      }
      for (const item of (role.selected_knowledge || [])) {
        const name = typeof item === 'object' ? item.element_name : item;
        if (name) all.push(name);
      }
    }
    return civilianizeCompetencies(all).slice(0, maxItems);
  };

  const coreCompetencies = computeCoreCompetencies(displayResume);

  // True only when the user has a real previously AI-generated resume to compare against.
  // Used to gate the "out of date" banner so it never fires on first-time creation.
  // Excludes: KSA-derived resume_bullets (auto-scaffolded from MOS selection),
  // empty/short/placeholder strings, and known UI fallback text.
  const isRealGeneratedText = (text) => {
    if (!text || typeof text !== 'string') return false;
    const trimmed = text.trim();
    if (trimmed.length < 20) return false;
    const lower = trimmed.toLowerCase();
    const placeholderSignals = [
      'summary will be generated',
      'summary content needs regeneration',
      'click regenerate',
    ];
    if (placeholderSignals.some(s => lower.includes(s))) return false;
    return true;
  };
  const hasPriorGeneratedResume = !!(
    isRealGeneratedText(displayResume?.professional_summary) ||
    (displayResume?.military_roles || []).some(r => isRealGeneratedText(r?.ai_summary_text))
  );

  // Column-first ordering to match PDF/print layout exactly.
  // Preview grid flows row-first, so we pre-arrange items so visual output matches
  // the PDF column-first distribution.
  const reorderForColumns = (items, cols) => {
    if (!items || items.length === 0) return [];
    const itemsPerCol = Math.ceil(items.length / cols);
    const out = [];
    for (let r = 0; r < itemsPerCol; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = c * itemsPerCol + r;
        if (idx < items.length) out.push(items[idx]);
        else out.push(null); // placeholder to preserve grid cell
      }
    }
    return out;
  };

  const getFilename = (format) => {
    const name = displayResume?.personal_info?.full_name || 'Resume';
    const parts = name.split(' ');
    if (parts.length >= 2) return `Resume_${parts[parts.length - 1]}_${parts[0]}.${format}`;
    return `Resume_${name.replace(/\s+/g, '_')}.${format}`;
  };

  const downloadFile = async (endpoint, format, setLoading) => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}${endpoint}`, { method: 'GET', credentials: 'include' });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getFilename(format);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 1000);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (error) {
      console.error(`[Download] ${format} failed:`, error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = () => downloadFile('/api/resume/download/pdf', 'pdf', setDownloadingPdf);
  const handleDownloadDocx = () => downloadFile('/api/resume/download/docx', 'docx', setDownloadingDocx);

  // ===== INLINE EDIT (manual prose edits, never touches stale-hash) =====
  const enterEditMode = useCallback(() => {
    if (!displayResume) return;
    setSummaryDraft(displayResume.professional_summary || '');
    const drafts = {};
    (displayResume.military_roles || []).forEach((role, idx) => {
      drafts[idx] = role?.ai_summary_text || '';
    });
    roleDraftsRef.current = drafts;
    setIsEditing(true);
  }, [displayResume]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setSummaryDraft('');
    roleDraftsRef.current = {};
  }, []);

  const handleSaveEdits = useCallback(async () => {
    if (!displayResume) return;
    setSavingEdits(true);
    try {
      const roles = Object.entries(roleDraftsRef.current).map(([idx, text]) => ({
        index: Number(idx),
        ai_summary_text: text || '',
      }));
      await axios.put(
        `${BACKEND_URL}/api/resume/manual-edit/save`,
        { professional_summary: summaryDraft || '', roles },
        { withCredentials: true }
      );
      const updatedRoles = (displayResume.military_roles || []).map((role, idx) => {
        if (roleDraftsRef.current[idx] === undefined) return role;
        return { ...role, ai_summary_text: roleDraftsRef.current[idx] || '', ai_summary_user_edited: true };
      });
      const updated = {
        ...displayResume,
        professional_summary: summaryDraft || '',
        professional_summary_user_edited: true,
        military_roles: updatedRoles,
      };
      setDisplayResume(updated);
      if (onResumeUpdate) onResumeUpdate(updated);
      setIsEditing(false);
      setSummaryDraft('');
      roleDraftsRef.current = {};
      toast.success('Resume saved');
    } catch (err) {
      toast.error('Failed to save resume');
    } finally {
      setSavingEdits(false);
    }
  }, [displayResume, onResumeUpdate, summaryDraft]);
  const handlePrint = async () => {
    // Fetch the backend-generated PDF and print it via a hidden iframe.
    // Printing a PDF document (not the HTML page) avoids browser header/footer artifacts.
    try {
      const response = await fetch(`${BACKEND_URL}/api/resume/download/pdf`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`PDF fetch failed: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create a hidden iframe, load the PDF, then print it
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            // Cross-origin fallback: open PDF in new tab for manual print
            window.open(blobUrl, '_blank');
          }
          // Cleanup after a delay to allow print dialog to appear
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(blobUrl);
          }, 60000);
        }, 500);
      };
    } catch (error) {
      console.error('[Print] Failed:', error);
      toast.error('Failed to prepare print. Try downloading the PDF instead.');
    }
  };

  const getZoomStyle = () => {
    if (zoomLevel === 'fit') return { transform: 'scale(1)', transformOrigin: 'top center' };
    return { transform: `scale(${zoomLevel})`, transformOrigin: 'top center', marginBottom: zoomLevel > 1 ? `${(zoomLevel - 1) * 100}%` : 0 };
  };

  // Format dates professionally
  const formatDateRange = (startMonth, startYear, endMonth, endYear, isCurrent) => {
    const fmt = (m, y) => {
      if (!m || !y) return String(y || '');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[(parseInt(m) - 1)] || m} ${y}`;
    };
    const start = fmt(startMonth, startYear);
    const end = isCurrent ? 'Present' : fmt(endMonth, endYear);
    return `${start} – ${end}`;
  };

  if (!displayResume?.personal_info) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="review-step">
        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6">
          <FileText className="w-10 h-10 text-slate-300" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">No Resume Data</h3>
        <p className="text-slate-500 max-w-sm">Please complete the Personal Info step first.</p>
      </div>
    );
  }

  const pi = displayResume.personal_info;
  const contactParts = [
    [pi.city, pi.state].filter(Boolean).join(', '),
    pi.phone,
    pi.email
  ].filter(Boolean);

  // Check for work activities and tools across all roles
  const workActivities = [];
  const toolCategories = [];
  for (const role of (displayResume.military_roles || [])) {
    for (const wa of (role.mos_data?.work_activities || [])) {
      if (wa.name && !workActivities.includes(wa.name)) workActivities.push(wa.name);
    }
    for (const t of (role.mos_data?.tools_technology || [])) {
      if (t.category && !toolCategories.includes(t.category)) toolCategories.push(t.category);
    }
  }

  return (
    <div data-testid="review-step" className="flex flex-col min-h-[600px] -mx-4 sm:-mx-6 lg:-mx-8 -mb-6">
      {/* Header with Actions */}
      <PreviewHeader
        title="Resume Preview"
        subtitle="Review and download your resume"
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        showZoom={true}
        data-testid="preview-header"
        styleControl={
          <div className="flex items-center gap-1.5" data-testid="resume-style-control">
            <span className="text-xs font-medium text-slate-500 hidden sm:inline">Style</span>
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1">
              {RESUME_STYLES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleStyleChange(s)}
                  className={`px-2.5 py-1 text-xs rounded capitalize transition-colors ${
                    resumeStyle === s
                      ? 'bg-white text-slate-900 shadow-sm font-medium'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`resume-style-${s}`}
                  aria-pressed={resumeStyle === s}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        }
        actions={displayResume.is_complete && (
          <>
            <Button onClick={handleDownloadPdf} disabled={downloadingPdf || isRegenerating} className="bg-brand hover:bg-brand-hover text-white" data-testid="download-pdf-btn">
              {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              PDF
            </Button>
            <Button onClick={handleDownloadDocx} disabled={downloadingDocx || isRegenerating} variant="outline" data-testid="download-docx-btn">
              {downloadingDocx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              DOCX
            </Button>
            {!isMobilePrintUnsupported() && (
              <Button onClick={handlePrint} disabled={isRegenerating} variant="outline" className="hidden sm:flex" data-testid="print-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            )}
          </>
        )}
      />


      {/* Status Notifications */}
      <div className="px-4 lg:px-8 print:hidden">
        {isRegenerating && isFirstTimeGenerating && (
          <FirstTimeGenerationBanner />
        )}
        {isRegenerating && !isFirstTimeGenerating && (
          <div className="mt-4 p-3 bg-brand-light border border-brand/20 rounded-lg" role="status" aria-live="polite" data-testid="regen-inprogress-banner">
            <div className="flex items-start gap-3">
              <Loader2 className="w-4 h-4 text-brand animate-spin mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brand">Regenerating your resume…</p>
                <p className="text-xs text-slate-600 mt-1">This may take a minute or longer. Please keep this page open.</p>
              </div>
            </div>
          </div>
        )}
        {pendingResumeRegen && !isRegenerating && hasPriorGeneratedResume && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="pending-regen-banner">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-amber-800">Your resume may be out of date because your information has changed.</p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => setShowRegenModal(true)} className="bg-brand hover:bg-brand-hover text-white text-xs" data-testid="regenerate-resume-btn">
                  Regenerate Resume
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  // PER-BROWSER WARNING SUPPRESSION ONLY. We remember the
                  // exact stale input hash the user dismissed so we don't
                  // nag them again on this device while inputs stay the
                  // same. We never call the server, never mark the resume
                  // fresh, and never touch `last_generated_input_hash`.
                  // Any further edit changes currentInputSig → dismissal
                  // no longer matches → the warning will return.
                  try {
                    if (displayResume?.resume_id) {
                      const sig = computeResumeInputSignature(displayResume);
                      if (sig) setDismissedHash(displayResume.resume_id, sig);
                    }
                  } catch (_) { /* fail-soft */ }
                  if (onDismissRegen) onDismissRegen();
                }} className="text-xs" data-testid="keep-current-resume-btn">
                  Keep Current
                </Button>
              </div>
            </div>
          </div>
        )}
        {regenError && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="regen-error-banner">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  {regenError === 'generation_timeout'
                    ? 'Resume generation is taking longer than expected. Please try again.'
                    : 'AI generation was temporarily unavailable. Your current resume content has been preserved.'}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={retryGeneration} disabled={isRegenerating} className="text-xs flex-shrink-0" data-testid="regen-retry-btn">
                {isRegenerating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Try Again
              </Button>
            </div>
          </div>
        )}
        {!displayResume.is_complete && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-amber-800 font-medium flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4" />
              Resume Incomplete - Complete the Military Career step with MOS validation to enable downloads.
            </p>
          </div>
        )}
      </div>

      {/* Paper Preview Canvas */}
      <div ref={containerRef} className="flex-1 bg-slate-200/70 overflow-auto print:overflow-visible print:bg-white" style={{ minHeight: '500px' }}>
        <ResumeTranslationFeedbackPrompt
          open={showFeedbackPrompt}
          onDismiss={() => {
            setShowFeedbackPrompt(false);
            markResumeTranslationFeedbackSeen(user?.user_id);
          }}
        />
        <div className="p-4 lg:p-8 flex justify-center print:p-0">
          <div className="w-full max-w-[720px]">
            {/* Edit Mode Toolbar — visually matches the Cover Letter editing toolbar */}
            {isEditing && (
              <div className="mb-4 flex items-center justify-between bg-white rounded-lg border border-slate-200 p-3 print:hidden">
                <div className="text-sm text-slate-600">
                  <p>
                    <Edit3 className="w-4 h-4 inline mr-1" />
                    Editing — changes save to your resume
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5" data-testid="edit-scope-hint">
                    Editable sections: Professional Summary and Military Experience narratives. Scroll down to edit more.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancelEdit}
                    disabled={savingEdits}
                    data-testid="cancel-edit-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveEdits}
                    disabled={savingEdits}
                    className="bg-brand hover:bg-brand-hover text-white"
                    data-testid="save-edit-btn"
                  >
                    {savingEdits ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                </div>
              </div>
            )}

            {/* Paper Frame */}
            <div
              className="bg-white shadow-2xl transition-transform duration-200 print:shadow-none relative"
              style={{ ...getZoomStyle(), width: '720px', minHeight: '932px', maxWidth: '100%' }}
              data-testid="resume-paper-frame"
            >
              {/* Edit button overlay — same placement/style as Cover Letter */}
              {!isEditing && displayResume.is_complete && !isRegenerating && (
                <button
                  onClick={enterEditMode}
                  className="absolute top-4 right-4 p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors z-10 print:hidden"
                  data-testid="edit-resume-btn"
                  aria-label="Edit Resume"
                >
                  <Edit3 className="w-4 h-4 text-slate-600" />
                </button>
              )}

              {/* === RESUME DOCUMENT === */}
              <div className="px-[var(--r-padx)] py-[var(--r-pady)]" style={{ ...resumeStyleVars(resumeStyle), fontFamily: 'var(--r-font)' }} data-testid="resume-document-root">

              {/* ── NAME & CONTACT ── */}
              <div className="mb-0" style={{ textAlign: 'var(--r-header-align)' }} data-testid="personal-info-header">
                <h1 className="text-[length:var(--r-name)] font-bold tracking-[var(--r-name-track)] text-slate-900 uppercase leading-tight">{pi.full_name}</h1>
                <p className="text-[length:var(--r-contact)] text-slate-600 mt-1.5 tracking-[0.03em] leading-relaxed">{contactParts.join('  \u00B7  ')}</p>
              </div>

              {/* Header rule */}
              <hr className="mt-3 mb-4" style={{ borderTopWidth: 'var(--r-hrule-w)', borderTopStyle: 'solid', borderTopColor: 'var(--r-hrule-c)' }} />

              {/* ── PROFESSIONAL SUMMARY ── */}
              {((displayResume.military_roles?.length > 0) || (displayResume.civilian_experience?.length > 0)) && (
                <section className="mb-[var(--r-section-gap)]" data-testid="professional-summary-section">
                  <div className="mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                    <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)]">
                      Professional Summary
                    </h2>
                  </div>
                  {isRegenerating && !displayResume.professional_summary && (
                    <div className="p-2 bg-brand-light border border-brand/10 rounded print:hidden">
                      <p className="text-[10px] text-brand flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Working...</p>
                    </div>
                  )}
                  {isEditing ? (
                    <EditableTextSurface
                      initialText={summaryDraft}
                      onTextChange={setSummaryDraft}
                      testId="summary-editor"
                      minHeight={56}
                    />
                  ) : displayResume.professional_summary && (() => {
                    const text = displayResume.professional_summary;
                    const lower = (text || '').toLowerCase();
                    const isError = lower.includes('<!doctype') || lower.includes('<html') || lower.includes('ratelimiterror') || lower.includes('rate_limit_exceeded');
                    if (isError) {
                      return <p className="text-[length:var(--r-body)] text-amber-700 italic">Summary content needs regeneration.</p>;
                    }
                    return (
                      <div className="flex items-start gap-2">
                        <p className="text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)] flex-1" data-testid="professional-summary-text">{text}</p>
                        <span className="inline-flex items-center gap-1 text-[9px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded flex-shrink-0 print:hidden">
                          <Sparkles className="w-2.5 h-2.5" /> AI
                        </span>
                      </div>
                    );
                  })()}
                  {!isEditing && !displayResume.professional_summary && !isRegenerating && (
                    <p className="text-slate-500 italic text-[length:var(--r-body)]">Your professional summary will appear here automatically once generated.</p>
                  )}
                </section>
              )}

              {/* ── CORE COMPETENCIES ── */}
              {coreCompetencies.length > 0 && (
                <section className="mb-[var(--r-section-gap)]" data-testid="core-competencies-section">
                  <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                    Core Competencies
                  </h2>
                  {/* CSS multi-column flows items column-first and lets each
                      item consume only its natural height. This avoids the
                      grid-row "tallest cell wins" gap when one entry wraps. */}
                  <div className="columns-3 gap-x-4 rounded-sm" style={{ background: 'var(--r-comp-shade)', padding: 'var(--r-comp-pad)' }}>
                    {coreCompetencies.map((comp, i) => (
                      <div
                        key={i}
                        className="text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)] break-inside-avoid mb-1"
                      >
                        {comp}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ── CORE RESPONSIBILITIES ── */}
              {workActivities.length > 0 && (
                <section className="mb-[var(--r-section-gap)]" data-testid="work-activities-section">
                  <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                    Core Responsibilities
                  </h2>
                  <div className="columns-2 gap-x-6">
                    {workActivities.slice(0, 8).map((activity, idx) => (
                      <div
                        key={idx}
                        className="text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)] flex items-start gap-1.5 break-inside-avoid mb-1"
                      >
                        <span className="text-slate-500 mt-[4px] text-[8px] flex-shrink-0">&#9642;</span>
                        <span className="flex-1">{activity}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ── TOOLS & SYSTEMS ── */}
              {toolCategories.length > 0 && (
                <section className="mb-[var(--r-section-gap)]" data-testid="tools-technology-section">
                  <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                    Tools & Systems
                  </h2>
                  <p className="text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)]">{toolCategories.slice(0, 8).join('  \u00B7  ')}</p>
                </section>
              )}

              {/* ── MILITARY EXPERIENCE ── */}
              {displayResume.military_roles?.length > 0 && (
                <section className="mb-[var(--r-section-gap)]">
                  <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                    Military Experience
                  </h2>
                  {displayResume.military_roles.map((role, idx) => {
                    const hasKsa = role.selected_knowledge?.length > 0 || role.selected_skills?.length > 0 || role.selected_abilities?.length > 0;
                    const summaryBullets = parseSummaryBullets(role.ai_summary_text);
                    // Fallback to stored resume_bullets when AI summary is unavailable (matches PDF logic)
                    const fallbackBullets = (!summaryBullets.length && Array.isArray(role.resume_bullets))
                      ? role.resume_bullets.map(b => (b || '').trim()).filter(Boolean)
                      : [];
                    const displayBullets = summaryBullets.length ? summaryBullets : fallbackBullets;
                    return (
                      <div key={idx} className="mb-4 last:mb-0" data-testid={`military-role-preview-${idx}`}>
                        <div className="flex justify-between items-baseline">
                          <div>
                            <p className="text-[12.5px] font-bold text-slate-900">{sanitizeMosTitle(role.mos_title, role.rank) || role.mos_code}</p>
                            <p className="text-[11px] text-slate-600 italic">{role.rank}, {role.branch}{role.location ? ` \u2014 ${role.location}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[11px] text-slate-600">{formatDateRange(role.start_month, role.start_year, role.end_month, role.end_year)}</span>
                            {hasKsa && role.ai_summary_text && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded print:hidden">
                                <Sparkles className="w-2.5 h-2.5" /> AI
                              </span>
                            )}
                          </div>
                        </div>
                        {isEditing ? (
                          <div className="mt-2">
                            <EditableTextSurface
                              initialText={(roleDraftsRef.current[idx] !== undefined ? roleDraftsRef.current[idx] : (role.ai_summary_text || ''))}
                              onTextChange={(t) => { roleDraftsRef.current[idx] = t; }}
                              testId={`role-editor-${idx}`}
                              minHeight={80}
                            />
                          </div>
                        ) : displayBullets.length > 0 && (
                          <ul className="mt-2 space-y-1 text-slate-700">
                            {displayBullets.map((bullet, bulletIdx) => (
                              <li key={bulletIdx} className="flex items-start gap-2 text-[length:var(--r-body)] leading-[var(--r-lh)]">
                                <span className="text-slate-500 mt-[3px] text-[8px] flex-shrink-0">&#9642;</span>
                                <span className="flex-1">{bullet}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {isRegenerating && hasKsa && !role.ai_summary_text && (
                          <div className="mt-1 p-1.5 bg-brand-light rounded flex items-center gap-2 text-[9px] text-brand print:hidden">
                            <Loader2 className="w-3 h-3 animate-spin" /> Working...
                          </div>
                        )}
                      </div>
                    );
                  })}
                </section>
              )}

              {/* ── PROFESSIONAL EXPERIENCE (paid) + VOLUNTEER EXPERIENCE ── */}
              {(() => {
                const civ = displayResume.civilian_experience || [];
                const paid = civ.filter((e) => !e.is_volunteer);
                const vol = civ.filter((e) => e.is_volunteer);
                const renderEntry = (exp, idx) => (
                  <div key={idx} className="mb-4 last:mb-0">
                    <div className="flex justify-between items-baseline">
                      <div>
                        <p className="text-[12.5px] font-bold text-slate-900">{exp.job_title}</p>
                        <p className="text-[11px] text-slate-600 italic">{exp.company}{exp.location ? `, ${exp.location}` : ''}</p>
                      </div>
                      <span className="text-[11px] text-slate-600 flex-shrink-0">{formatDateRange(exp.start_month, exp.start_year, exp.end_month, exp.end_year, exp.is_current)}</span>
                    </div>
                    {exp.description && (
                      <p className="mt-1.5 text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)]">{exp.description}</p>
                    )}
                  </div>
                );
                return (
                  <>
                    {hasMeaningfulEntries(paid, ['job_title', 'company', 'description']) && (
                      <section className="mb-[var(--r-section-gap)]" data-testid="review-professional-experience">
                        <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                          Professional Experience
                        </h2>
                        {paid.map(renderEntry)}
                      </section>
                    )}
                    {hasMeaningfulEntries(vol, ['job_title', 'company', 'description']) && (
                      <section className="mb-[var(--r-section-gap)]" data-testid="review-volunteer-experience">
                        <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                          Volunteer Experience
                        </h2>
                        {vol.map(renderEntry)}
                      </section>
                    )}
                  </>
                );
              })()}

              {/* ── EDUCATION ── */}
              {hasMeaningfulEntries(displayResume.education, ['degree_or_cert', 'institution']) && (
                <section className="mb-[var(--r-section-gap)]">
                  <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                    Education
                  </h2>
                  {displayResume.education.map((edu, idx) => (
                    <div key={idx} className="mb-2 last:mb-0 flex justify-between items-baseline">
                      <div>
                        <p className="text-[12px] font-semibold text-slate-900">{edu.degree_or_cert}</p>
                        <p className="text-[11px] text-slate-600 italic">{edu.institution}</p>
                      </div>
                      {edu.end_year && <span className="text-[11px] text-slate-600 flex-shrink-0">{edu.end_year}</span>}
                    </div>
                  ))}
                </section>
              )}

              {/* ── CERTIFICATIONS & LICENSES ── (structured preferred; legacy fallback) */}
              {(() => {
                const MON = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const fmtExp = (exp) => {
                  if (!exp) return null;
                  const m = /^(\d{4})-(\d{1,2})$/.exec(exp.trim());
                  if (m) { const mo = parseInt(m[2], 10); return mo >= 1 && mo <= 12 ? `${MON[mo]} ${m[1]}` : m[1]; }
                  return /^\d{4}$/.test(exp.trim()) ? exp.trim() : exp.trim();
                };
                const structured = (displayResume.professional_credentials || []).filter((c) => c && (c.name || '').trim());
                let lines = [];
                if (structured.length > 0) {
                  lines = structured.map((c) => {
                    let t = c.name.trim();
                    if (c.issuer && c.issuer.trim()) t += ` \u2014 ${c.issuer.trim()}`;
                    const e = fmtExp(c.expiration_date);
                    if (e) t += ` (Expires ${e})`;
                    return t;
                  });
                } else {
                  lines = (displayResume.certifications || []).filter((c) => c && c.trim()).map((c) => c.trim());
                }
                if (lines.length === 0) return null;
                const compact = lines.every((l) => !l.includes('\u2014') && !l.includes('Expires'));
                return (
                  <section className="mb-[var(--r-section-gap)]" data-testid="review-certifications">
                    <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                      Certifications & Licenses
                    </h2>
                    {compact && lines.length > 1 ? (
                      <p className="text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)]">{lines.join('  \u00B7  ')}</p>
                    ) : (
                      <ul className="space-y-1">
                        {lines.map((l, idx) => (
                          <li key={idx} className="text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)] flex items-start gap-1.5">
                            <span className="text-slate-500 mt-[3px] text-[8px]">&#9642;</span>
                            {l}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })()}

              {/* ── ADDITIONAL QUALIFICATIONS ── consolidates Security Clearance, Military
                  Qualifications, and Honors/Recognition into labeled rows (empty rows suppressed).
                  Preserves data ownership; never merges military/civilian; never relabels legacy. */}
              {(() => {
                const LEVEL = { confidential: 'Confidential', secret: 'Secret', top_secret: 'Top Secret', ts_sci: 'Top Secret / SCI', q_clearance: 'Q Clearance', l_clearance: 'L Clearance', doe_l: 'DOE L', doe_q: 'DOE Q', other: 'Other' };
                const PREFIX = { current_active: '', interim: 'Interim ', previously_held: 'Previously Held ' };
                const catOf = (sa) => sa.category || awardCatalog[sa.award_id]?.category;
                const nameOf = (sa) => awardCatalog[sa.award_id]?.display_name || sa.display_name || sa.award_id;
                const structured = displayResume.structured_awards || [];
                const quals = structured.filter((sa) => QUAL_CATS.has(catOf(sa)));
                const decos = structured.filter((sa) => !QUAL_CATS.has(catOf(sa)));

                // Security clearance (single consolidated value)
                let clearanceVal = '';
                const career = (displayResume.security_clearance || '').trim();
                if (career && career.toLowerCase() !== 'none') {
                  const sci = !!displayResume.security_clearance_sci;
                  clearanceVal = (career === 'ts_sci' || (career === 'top_secret' && sci))
                    ? 'Top Secret with SCI'
                    : (LEVEL[career] || career.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
                } else {
                  for (const r of (displayResume.military_roles || [])) {
                    const lvl = (r.security_clearance || '').trim();
                    if (!lvl || lvl.toLowerCase() === 'none') continue;
                    const label = LEVEL[lvl] || lvl.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                    clearanceVal = `${PREFIX[r.security_clearance_status] || ''}${label}`.trim();
                    break;
                  }
                }

                const military = decos.map((sa) => `${nameOf(sa)}${(sa.quantity || 1) > 1 ? ` (${sa.quantity}x)` : ''}`);
                const civilian = (displayResume.additional_awards_text || '')
                  .split('\n').map((a) => a.trim().replace(/^[•\-*]\s*/, '')).filter(Boolean);
                let additional = [];
                if (displayResume.awards && structured.length === 0) {
                  additional = displayResume.awards.includes('\n')
                    ? displayResume.awards.split('\n').map((a) => a.trim().replace(/^[•\-*]\s*/, '')).filter(Boolean)
                    : [displayResume.awards.trim()].filter(Boolean);
                }

                const rows = [
                  clearanceVal && { label: 'Security Clearance', value: clearanceVal },
                  quals.length > 0 && { label: 'Military Qualifications', value: quals.map(nameOf).join(' · ') },
                  military.length > 0 && { label: 'Military Honors', value: military.join(' · ') },
                  civilian.length > 0 && { label: 'Civilian Recognition', value: civilian.join(' · ') },
                  additional.length > 0 && { label: 'Additional Recognition', value: additional.join(' · ') },
                ].filter(Boolean);
                if (rows.length === 0) return null;
                return (
                  <section className="mb-[var(--r-section-gap)]" data-testid="review-additional-qualifications">
                    <h2 className="text-[length:var(--r-heading)] font-bold text-[color:var(--r-heading-color)] uppercase tracking-[var(--r-heading-track)] mb-2 pb-1.5 border-b-[length:var(--r-srule-w)] border-[color:var(--r-srule-c)]">
                      Additional Qualifications
                    </h2>
                    <div className="space-y-1">
                      {rows.map((r) => (
                        <p key={r.label} data-testid={`aq-row-${r.label.toLowerCase().replace(/\s+/g, '-')}`} className="text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)]">
                          <span className="font-semibold text-slate-800">{r.label}:</span> {r.value}
                        </p>
                      ))}
                    </div>
                  </section>
                );
              })()}

            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Regeneration Confirmation Modal */}
      <Dialog open={showRegenModal} onOpenChange={(open) => { if (!isRegenerating) setShowRegenModal(open); }}>
        <DialogContent
          className="max-w-md"
          onInteractOutside={(e) => { if (isRegenerating) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (isRegenerating) e.preventDefault(); }}
        >
          {isRegenerating ? (
            <div data-testid="regen-modal-inprogress-state" role="status" aria-live="polite" className="py-2">
              <DialogHeader>
                <DialogTitle>Regenerating your resume…</DialogTitle>
              </DialogHeader>
              <div className="flex items-start gap-3 mt-3">
                <Loader2 className="w-5 h-5 text-brand animate-spin mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-600">This may take a minute or longer. Please keep this page open.</p>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Regenerate Resume?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600 mb-2">
                Regenerating will replace your current resume content with a new version based on your latest military service information. Before continuing, you may want to save the current version.
              </p>
              <p className="text-sm font-medium text-slate-700 mb-4" data-testid="regen-modal-duration-note">
                Regeneration usually takes 30–60 seconds, but may take longer.
              </p>
              <div className="flex flex-col gap-2 mb-4">
                <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloadingPdf} className="justify-start" data-testid="regen-modal-download-btn">
                  <Download className="w-4 h-4 mr-2" /> Download Current Resume (PDF)
                </Button>
                {!isMobilePrintUnsupported() && (
                  <Button variant="outline" size="sm" onClick={handlePrint} className="justify-start" data-testid="regen-modal-print-btn">
                    <Printer className="w-4 h-4 mr-2" /> Print Current Resume
                  </Button>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowRegenModal(false)} data-testid="regen-modal-cancel-btn">Cancel</Button>
                <Button size="sm" onClick={() => runRegeneration('manual')} disabled={isRegenerating} className="bg-brand hover:bg-brand-hover text-white" data-testid="regen-modal-continue-btn">
                  Continue and Regenerate
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * FirstTimeGenerationBanner — richer wait state shown ONLY for the
 * initial AI generation of a user's resume (first time they reach Step 7
 * with KSAs selected). Rotates a small set of reassurance lines so the
 * user has clear visual progress signals during the LLM call (which may take
 * about a minute). No false percent progress and no false time promise.
 */
function FirstTimeGenerationBanner() {
  const STEPS = [
    'Translating your military experience',
    'Building your professional summary',
    'Organizing your skills and responsibilities',
    'Preparing your resume preview',
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % STEPS.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      className="mt-4 p-4 bg-brand-light border border-brand/20 rounded-lg"
      data-testid="first-time-gen-banner"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <Loader2 className="w-5 h-5 text-brand animate-spin mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand">Generating your resume…</p>
          <p className="text-xs text-slate-600 mt-1">
            Translating your military experience and building your professional summary. This may take about a minute. Please don&apos;t close this page.
          </p>
          <p className="text-xs text-slate-500 mt-2 transition-opacity duration-500" data-testid="first-time-gen-step">
            {STEPS[idx]}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * EditableTextSurface — contentEditable that owns its DOM.
 *
 * Same one-time-seed pattern used in CoverLetterDocument's
 * EditableLetterSurface. We seed innerText imperatively exactly once per
 * edit-mount and never re-apply it from React state while typing — that's
 * what was causing the cursor to jump back to the start of the document
 * in the old cover letter editor. Parent receives every change via
 * onInput → onTextChange.
 */
function EditableTextSurface({ initialText, onTextChange, testId, minHeight = 56 }) {
  const ref = useRef(null);
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!ref.current) return;
    ref.current.innerText = initialText || '';
    seededRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className="text-[length:var(--r-body)] text-slate-700 leading-[var(--r-lh)] outline-none focus:outline-none border border-brand/30 rounded p-2 bg-brand-light/40 whitespace-pre-wrap"
      style={{ minHeight: `${minHeight}px` }}
      onInput={(e) => onTextChange?.(e.currentTarget.innerText)}
      data-testid={testId || 'resume-editor'}
    />
  );
}

export default ReviewStep;
